/// <amd-module name="TFS/Admin/Views/OrganizationPolicies/OrganizationPolicies" />
import React = require("react");
import AdminResources = require("TFS/Admin/Views/OrganizationPolicies/Resources");

import * as StringUtil from "VSS/Core/Util/String";
import * as models from "TFS/Admin/Views/OrganizationPolicies/Contracts/DataModels";

import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { Components, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { CustomHeader, HeaderTitle, HeaderTitleArea, TitleSize } from "VSSUI/Header";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ZeroData } from "VSSUI/ZeroData";

import { IConfirmationDialogProps } from "TFS/Admin/Common/Controls/ConfirmationDialog/ConfirmationDialog.Props";
import { IRequestAccessDialogProps } from "TFS/Admin/Common/Controls/RequestAccessDialog/RequestAccessDialog.Props";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { List, ListItem, IListItemDetails } from "VSSUI/List";
import { ObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { OrganizationPoliciesSource } from "TFS/Admin/Views/OrganizationPolicies/Sources/OrganizationPoliciesSource";
import { Page } from "VSSUI/Page";
import { Policy } from "TFS/Admin/Views/OrganizationPolicies/Policy";
import { getAssetLocation } from "VSS/Platform/Location";

export class OrganizationPoliciesComponent extends VssComponent<{}, models.OrganizationPoliciesComponentState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private performanceRecorded = false;
    private _source: OrganizationPoliciesSource;
    private showConfirmationDialog: ObservableValue<boolean>;
    private showRequestAccessDialog: ObservableValue<boolean>;
    private dialogMessage?: models.DialogMessage;
    private policyUpdateConfiguration?: models.PolicyUpdateConfig;    
    private requestAccessMessage?: models.RequestAccessMessage;
    private errorMessage: string;
    protected isGuestUser: boolean;
    protected hostId: string;
    protected requestAccessUrl: string;

    public constructor(props: {}, context?: any) {
        super(props, context);

        this.state = {
            showErrorMessage: false,
            updateInProgress: false,
            data: {} as models.PoliciesData
        };

        this.errorMessage = "";
        this._source = new OrganizationPoliciesSource();
        this.showConfirmationDialog = new ObservableValue<boolean>(false);
        this.showRequestAccessDialog = new ObservableValue<boolean>(false);
    }

    public componentDidMount() {
        this.getPolicies();
    }

    private getPolicies = (): void => {
        var telemetry = this._startActionCapture("FetchFromDataProvider", {});
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        var data = contributionService.getData<models.PoliciesData>("ms.vss-admin-web.organization-policies-data-provider")!;
        if (data) {
            this.isGuestUser = data.isGuestUser;
            this.hostId = data.hostId;
            this.requestAccessUrl = data.requestAccessUrl;

            this.setState({ data: data });
        } else {
            telemetry.captureError("ErrorFromDataProvider");
        }
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationPolicies, Feature.OrganizationPolicies, action, properties);
    };

    public render(): JSX.Element {
        if (!this.performanceRecorded && this.state.data.policies) {
            const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Area.Admin, Feature.OrganizationPolicies);
            this.performanceRecorded = true;
        }

        let hasPolicyPermissions = this.state.data.permissionBits & models.PermissionConstants.MODIFY_COLLECTION;

        let list: JSX.Element[] = [];
        for (let policyTypeKey in this.state.data.policies) {
            const policyType = policyTypeKey as models.PolicyType;
            list.push(
                <section key={policyType} role="region">
                    <CustomHeader>
                        <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Small}>
                            {this._source.getPolicyTitleText(policyType)}
                        </HeaderTitle>
                    </CustomHeader>
                    <List
                        itemProvider={{ value: this.state.data.policies[policyType], length: this.state.data.policies[policyType].length }}
                        renderRow={(index, item, details) => this.renderPolicyRow(index, item, details, policyType, hasPolicyPermissions)}
                    />
                </section>
            );
        }

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column policies-page bolt-page-white">
                    <CustomHeader>
                        <HeaderTitleArea className="flex-column">
                            <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Large}>
                                {AdminResources.PoliciesPageTitle}
                            </HeaderTitle>
                        </HeaderTitleArea>
                    </CustomHeader>
                    <div className="flex-column flex-grow">
                        {this.state.showErrorMessage && this._showActionErrorMessage()}
                        {this.state.updateInProgress ? (
                            <Spinner className="flex-row flex-grow" size={SpinnerSize.large} />
                        ) : (
                            <>
                                {list.length > 0 ? list : this._showZeroDataMessage()}
                                <Observer showConfirmationDialog={this.showConfirmationDialog}>
                                    {(observableProps: { showConfirmationDialog: boolean }) =>
                                        observableProps.showConfirmationDialog && (
                                            <WrappedComponent<IConfirmationDialogProps>
                                                dependencies={["ms.vss-admin-web.admin-common"]}
                                                wrappedType={"ms.vss-admin-web.confirmation-dialog"}
                                                wrappedProps={{
                                                    onCancel: this.confirmationCancel,
                                                    onItemConfirm: this.onConfirmation,
                                                    dialogTitle: this.dialogMessage != undefined ? this.dialogMessage.title : "",
                                                    confirmationMessage:
                                                        this.dialogMessage != undefined ? this.dialogMessage.confirmationMessage : "",
                                                    warningMessage: this.dialogMessage != undefined ? this.dialogMessage.warningMessage : undefined,
                                                    confirmationButtonLabel: AdminResources.SaveButtonLabel,
                                                    learnMoreLink: this.dialogMessage != undefined ? this.dialogMessage.learnMoreLink : undefined
                                                }}
                                            />
                                        )
                                    }
                                </Observer>
                                <Observer showRequestAccessDialog={this.showRequestAccessDialog}>
                                    {(observableProps: { showRequestAccessDialog: boolean }) =>
                                        observableProps.showRequestAccessDialog && (
                                            <WrappedComponent<IRequestAccessDialogProps>
                                                dependencies={["ms.vss-admin-web.admin-common"]}
                                                wrappedType={"ms.vss-admin-web.requestaccess-dialog"}
                                                wrappedProps={{
                                                    onCancel: this._onRequestAccessCancel,
                                                    onSave: (url: string) => {
                                                        this._onRequestAccessSave(url);
                                                    },
                                                    requestAccessTitle: this.requestAccessMessage != undefined ? this.requestAccessMessage.requestAccessTitle : "",
                                                    requestAccessMessage: this.requestAccessMessage != undefined ? this.requestAccessMessage.requestAccessMessage : "",
                                                    saveButtonLabel: AdminResources.SaveButtonLabel,
                                                    requestAccessUrl: this.requestAccessUrl,
                                                    requestAccessLearnMoreLink: this.requestAccessMessage != undefined ? this.requestAccessMessage.requestAccessLearnMoreLink : ""
                                                }}
                                            />
                                        )
                                    }
                                </Observer>
                            </>
                        )}
                    </div>
                </Page>
            </Surface>
        );
    }

    private renderPolicyRow = (
        index: number,
        item: models.PolicyData,
        details: IListItemDetails<models.PolicyData>,
        policyType: models.PolicyType,
        hasPolicyPermissions: number
    ): JSX.Element => {
        var hasUrl = false;
        if (item.policy.name == models.PolicyNames.AllowRequestAccess) {
            hasUrl = true;
        }

        return (
            <ListItem key={"li-" + index} index={index} details={details}>
                <Policy
                    key={policyType + index.toString()}
                    policy={item}
                    keyId={index.toString()}
                    onChangeHandler={(key: string, value: boolean, enforce: boolean) => {
                        this.onPolicyChanged(
                            this.state.data.policies[policyType][+key],
                            policyType,
                            key,
                            value,
                            enforce,
                            this.state.data.invertedPolicies
                        );
                    }}
                    hasPolicyPermissions={hasPolicyPermissions != 0}
                    isOrganizationActivated={this.state.data.isOrganizationActivated}
                    hasUrl= {hasUrl}
                    onEditUrlClick={(policyDescription: string, policyLink: string) => this.onEditUrlClick(policyDescription, policyLink)}
                />                
            </ListItem>
        );
    };

    protected onEditUrlClick = (policyDescription: string, policyLink: string) => {
        this.requestAccessMessage = {
            requestAccessTitle: AdminResources.RequestAccessPolicyHeader,
            requestAccessMessage: AdminResources.RequestAccessPolicyDescription,
            requestAccessLearnMoreLink: policyLink
        };
        this.showRequestAccessDialog.value = true;
    }

    protected onPolicyChanged = (
        policy: models.IPolicy,
        section: models.PolicyType,
        key: string,
        value: boolean,
        enforce: boolean,
        invertedPolicies: string[]
    ) => {
        var telemetry = this._startActionCapture("PolicyChanged", {});
        const isEnforceChange = enforce !== !!policy.policy.enforce; // Take a look

        this.policyUpdateConfiguration = {
            policy,
            section,
            key,
            value,
            enforce,
            invertedPolicies
        };

        if (policy.policy.name === models.PolicyNames.AllowAnonymousAccess || policy.policy.name === models.PolicyNames.AllowOrgAccess) {
            let warningMessage: string | undefined = undefined;
            switch (policy.policy.name) {
                case models.PolicyNames.AllowAnonymousAccess:
                    warningMessage = value ? AdminResources.SetAnonymousAccessWarningMessage : AdminResources.SetAnonymousAccessOffWarningMessage;
                    break;
                case models.PolicyNames.AllowOrgAccess:
                    warningMessage = value ? AdminResources.SetEnterpriseVisibleWarningMessage : AdminResources.SetEnterpriseNonVisibleWarningMessage;
                    break;
            }

            if (!isEnforceChange) {
                warningMessage = AdminResources.ChangePolicyDetail + " " + warningMessage;
            }

            this.dialogMessage = {
                title: AdminResources.ConfirmPolicyHeader,
                confirmationMessage: StringUtil.format(
                    AdminResources.PolicyDescription,
                    isEnforceChange ? (enforce ? AdminResources.Enforce : AdminResources.NoLongerEnforce) : AdminResources.Change,
                    policy.description,
                    value ? AdminResources.On : AdminResources.Off
                ),
                warningMessage: warningMessage
            };
            this.showConfirmationDialog.value = true;
        } else if (policy.policy.name === models.PolicyNames.DisallowAadGuestUserAccess && !value && this.isGuestUser) {
            this.errorMessage = AdminResources.DisableExternalGuestAccessAsGuestError;
            this.setState({ updateInProgress: false, showErrorMessage: true, data: this.state.data });
            telemetry.captureError("DisableExternalGuestAccessAsGuestError");
        } else if (policy.policy.name === models.PolicyNames.AllowRequestAccess && !value) {
            this.requestAccessMessage = {
                requestAccessTitle: AdminResources.RequestAccessPolicyHeader,
                requestAccessMessage: AdminResources.RequestAccessPolicyDescription,
                requestAccessLearnMoreLink: policy.learnMoreLink
            };
            this.showRequestAccessDialog.value = true;
        } else {
            this.showConfirmationDialog.value = false;
            this.policyChangeRequested(this.policyUpdateConfiguration);
        }
        telemetry.captureTelemetry({ ShowConfirmationDialog: this.showConfirmationDialog.value });
    };

    private policyChangeRequested(config: models.PolicyUpdateConfig) {
        var telemetry = this._startActionCapture("PolicyChangeRequested", {});
        this.showConfirmationDialog.value = false;
        this.setState({ updateInProgress: true });
        var currentData = this.state.data;

        let newPolicy = clone(config.policy);
        newPolicy.policy.effectiveValue = config.value;
        newPolicy.policy.enforce = config.enforce;

        this._source.updatePolicy(this.context.pageContext, newPolicy.policy, config.invertedPolicies).then(
            () => {
                currentData.policies[config.section][+config.key] = newPolicy;
                this.setState({ updateInProgress: false, data: currentData, showErrorMessage: false });
            },
            (reason?: any) => {
                this.errorMessage = StringUtil.format(AdminResources.SavingError, reason.message || String(reason));
                this.setState({ updateInProgress: false, showErrorMessage: true, data: currentData });
                telemetry.captureError("ErrorSavingPolicy");
            }
        );
    }

    private confirmationCancel = (): void => {
        this.showConfirmationDialog.value = false;
        if (this.policyUpdateConfiguration) {
            this.policyUpdateConfiguration = undefined;
            this.forceUpdate();
        }
    };

    private onConfirmation = (): void => {
        if (this.policyUpdateConfiguration) {
            this.policyChangeRequested(this.policyUpdateConfiguration);
        }
    };

    private policyChangeRequestedWithProperty(config: models.PolicyUpdateConfig, url: string) {
        var telemetry = this._startActionCapture("PolicyChangeRequested", {});
        this.showRequestAccessDialog.value = false;
        this.setState({ updateInProgress: true });
        var currentData = this.state.data;

        let newPolicy = clone(config.policy);
        newPolicy.policy.effectiveValue = config.value;
        newPolicy.policy.enforce = config.enforce;

        this._source.updateProperties(this.context.pageContext, url, this.hostId).then(
            () => {
                this._source.updatePolicy(this.context.pageContext, newPolicy.policy, config.invertedPolicies).then(
                    () => {
                        currentData.policies[config.section][+config.key] = newPolicy;
                        this.setState({ updateInProgress: false, data: currentData, showErrorMessage: false });
                        this.requestAccessUrl = url;
                    },
                    (reason?: any) => {
                        this.errorMessage = StringUtil.format(AdminResources.SavingError, reason.message || String(reason));
                        this.setState({ updateInProgress: false, showErrorMessage: true, data: currentData });
                        telemetry.captureError("ErrorSavingPolicy");
                    }
                )
            },
            (reason?: any) => {
                this.errorMessage = StringUtil.format(AdminResources.SavingError, reason.message || String(reason));
                this.setState({ updateInProgress: false, showErrorMessage: true, data: currentData });
                telemetry.captureError("ErrorSavingPolicy");
            }
        );
    }

    
    private _urlChangeRequested(url: string) {
        var telemetry = this._startActionCapture("UrlChangeRequested", {});
        this.showRequestAccessDialog.value = false;
        
        this._source.updateProperties(this.context.pageContext, url, this.hostId).then(
            () => {                
                this.requestAccessUrl = url;
            },
            (reason?: any) => {
                this.errorMessage = StringUtil.format(AdminResources.SavingError, reason.message || String(reason));
                telemetry.captureError("ErrorSavingPolicy");
            }
        );
    }

    private _onRequestAccessCancel = (): void => {
        this.showRequestAccessDialog.value = false;
        if (this.policyUpdateConfiguration) {
            this.policyUpdateConfiguration = undefined;
            this.forceUpdate();
        }
    };

    private _onRequestAccessSave = (url: string): void => {
        if (this.policyUpdateConfiguration) {
            this.policyChangeRequestedWithProperty(this.policyUpdateConfiguration, url);
        }
        else if (url != null)
        {
            this._urlChangeRequested(url);
        }
    };

    private _showActionErrorMessage() {
        return (
            <MessageCard
                className="message-warning-card"
                severity={MessageCardSeverity.Error}
                onDismiss={() => this.setState({ showErrorMessage: false })}
            >
                <span> {this.errorMessage}</span>
            </MessageCard>
        );
    }

    private _showZeroDataMessage() {
        return (
            <ZeroData
                imagePath={getAssetLocation(
                    this.context.pageContext,
                    "ms.vss-tfs-web/platform-content/Illustrations/general-no-results-found.svg"
                )}
                imageAltText={AdminResources.ZeroData}
                primaryText={AdminResources.ZeroData}
            />
        );
    }
}

export function clone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

Components.add("organizationPolicies", OrganizationPoliciesComponent);
